import React, { useMemo, useRef, useState } from 'react';
import dayjs from 'dayjs';
import { useRequest } from 'ahooks';
import { Button, Tag, Space, Tooltip } from '@bedrock/components';
import { ProCard, ProTablePlus } from '@bedrock/eed-react';
import { FilterType } from '@bedrock/eed-react/lib/components/ProFilter/interface';
import { IProTableRef } from '@bedrock/eed-react/lib/components/ProTable/interface';
import CopyableNode from '@mpc/common/src/components/CopyableNode';
import {
  fetchAccidentList,
  fetchAccidentTabCounts,
} from '../services/index';
import { supplierDefs } from '@purchase/supplier';

// 列表项运行期还携带 oaNo（OA 流程跳转）与 flowState（已驳回状态判断）：
// 列表 DTO 未声明这些字段（AccidentDetailDto 已声明 oaNo?/flowState?），故本地补齐。
// accidentId / oaNo 是列表行必有的导航字段，声明为非可选。
type AccidentListItem = supplierDefs.AccidentListItemDto & {
  accidentId: number;
  oaNo: string;
  flowState?: string;
};

// TODO: 待对齐真实接口后同步调整筛选条件字段名

/** Tab 配置：待处理 / 全部（协同端） */
const TAB_KEYS = {
  PENDING: 'PENDING',
  ALL: 'ALL',
} as const;
type TabKey = (typeof TAB_KEYS)[keyof typeof TAB_KEYS];

/** 协同端状态 code → { name, color } */
const STATUS_OPTIONS = [
  { value: '4', name: '待处理', color: 'light-orange' },
  { value: '5', name: '审批中', color: 'light-blue' },
  { value: '6', name: '归档', color: 'light-green' },
  { value: '7', name: '已终止', color: 'light-red' },
  { value: 'RETURNED', name: '已驳回', color: 'light-red' },
];
const STATUS_NAME_MAP: Record<string, string> = Object.fromEntries(
  STATUS_OPTIONS.map((s) => [s.value, s.name]),
);
const STATUS_COLOR_MAP: Record<string, string> = Object.fromEntries(
  STATUS_OPTIONS.map((s) => [s.value, s.color]),
);

/** 事故类型筛选选项（value 对齐 AccidentLevel @EnumSchema） */
const ACCIDENT_TYPE_OPTIONS = [
  { name: '重大违法违规', value: '重大违法违规' },
  { name: '触犯供应商管理红线', value: '触犯供应商管理红线' },
  { name: '重大工作失误', value: '重大工作失误' },
  { name: '影响合作公平性及合作流程的行为', value: '影响合作公平性及合作流程的行为' },
];

/** 即将截止阈值（天）：整改截至时间距今 ≤ 该天数时展示"即将截止" */
const DEADLINE_SOON_DAYS = 10;

type Props = {
  /** 供应商 ID（external 请求头已自动带 Vendor-Id，此处透传用于契约对齐） */
  vendorId?: string;
  /** 查看详情回调（跳协同端事故详情页），由父级决定跳转路径；传事故 accidentId */
  onViewDetail: (accidentId: number) => void;
  /** 处理回调（跳 OA 反馈流程），由父级决定跳转路径；传事故 oaNo */
  onProcess: (oaNo: string) => void;
};

const AccidentList: React.FC<Props> = ({ vendorId, onViewDetail, onProcess }) => {
  const tableRef = useRef<IProTableRef<any>>();
  const [activeTab, setActiveTab] = useState<TabKey>(TAB_KEYS.PENDING);

  // Tab 计数角标
  const { data: tabCounts } = useRequest(() => fetchAccidentTabCounts());
  const counts = tabCounts?.data;

  const queryService = async ({ params }: any) => {
    const { current, pageSize, paramsWithConditions = {}, keyword } = params || {};

    const cond: Record<string, any> = {};
    Object.entries(paramsWithConditions).forEach(([key, val]: [string, any]) => {
      const v = val?.value;
      if (v !== undefined && v !== '' && v !== null) {
        cond[key] = v;
      }
    });

    // 日期范围 → ISO-8601 字符串（ProTablePlus DATE_RANGE 产出 dayjs.valueOf() 毫秒数，
    // 后端 ZonedDateTime 反序列化只认 ISO-8601，纯数字当纳秒解析 → 垃圾时间）
    const toRange = (range?: (string | number)[]) => {
      if (!range || range.length !== 2) return { start: undefined, end: undefined };
      return {
        start: range[0] != null ? dayjs(range[0]).format('YYYY-MM-DDTHH:mm:ssZ') : undefined,
        end: range[1] != null ? dayjs(range[1]).format('YYYY-MM-DDTHH:mm:ssZ') : undefined,
      };
    };
    const createTime = toRange(cond.createTimeRange);
    const occurDate = toRange(cond.occurDateRange);
    const discoverDate = toRange(cond.discoverDateRange);
    const notifyTime = toRange(cond.notifyTimeRange);
    const rectifyDeadline = toRange(cond.rectifyDeadlineRange);

    const res = await fetchAccidentList({
      pageNo: current || 1,
      pageSize: pageSize || 20,
      scope: activeTab === TAB_KEYS.PENDING ? 'PENDING' : 'ALL',
      accidentNo: keyword || cond.accidentNo,
      accidentTypes: cond.accidentTypes,
      statuses: cond.statuses,
      createTimeStart: createTime.start,
      createTimeEnd: createTime.end,
      occurDateStart: occurDate.start,
      occurDateEnd: occurDate.end,
      discoverDateStart: discoverDate.start,
      discoverDateEnd: discoverDate.end,
      notifyTimeStart: notifyTime.start,
      notifyTimeEnd: notifyTime.end,
      rectifyDeadlineStart: rectifyDeadline.start,
      rectifyDeadlineEnd: rectifyDeadline.end,
    });

    return {
      data: {
        list: res.data?.records || [],
        total: res.data?.total || 0,
      },
    };
  };

  const columns = useMemo(
    () => [
      {
        title: '事故编号',
        dataIndex: 'accidentNo',
        width: 200,
        fixed: 'left' as const,
        render: (_: any, record: AccidentListItem) => {
          const ops = record.operationTypes || [];
          return (
            <CopyableNode value={record.accidentNo}>
              <Space size={4}>
                <Button
                  type="link"
                  onClick={() => {
                    if (ops.includes('handle')) {
                      onProcess(record.oaNo);
                    } else {
                      onViewDetail(record.accidentId);
                    }
                  }}>
                  {record.accidentNo || '--'}
                </Button>
                {record.urgentFlag && <Tag color="red" size="small" text="急" />}
              </Space>
            </CopyableNode>
          );
        },
      },
      {
        title: '事故类型',
        dataIndex: 'accidentTypes',
        width: 260,
        searchRender: {
          name: '事故类型',
          value: 'accidentTypes',
          showCondition: false,
          type: FilterType.SELECT_MULTIPLE,
          filterComponentProps: { options: ACCIDENT_TYPE_OPTIONS },
        },
        render: (v: string[]) => {
          if (!Array.isArray(v) || !v.length) return '--';
          const tagStyle: React.CSSProperties = { maxWidth: 100, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'inline-block', verticalAlign: 'middle' };
          const renderTag = (t: string) => (
            <Tooltip key={t} title={t}>
              <Tag isElliptical color="light-grey" text={t} style={tagStyle} />
            </Tooltip>
          );
          if (v.length === 1) return renderTag(v[0]);
          const [first, ...rest] = v;
          return (
            <Space size={4}>
              {renderTag(first)}
              <Tooltip
                title={
                  <div className="flex flex-col gap-1">
                    {v.map((t) => (
                      <span key={t}>{t}</span>
                    ))}
                  </div>
                }
              >
                <Tag color="light-grey" text={`+${rest.length}`} className="cursor-default" />
              </Tooltip>
            </Space>
          );
        },
      },
      {
        title: '处理决定',
        dataIndex: 'handleTypes',
        width: 220,
        render: (v: string[]) =>
          Array.isArray(v) && v.length ? (
            <span>{v.join('，')}</span>
          ) : (
            '--'
          ),
      },
      {
        title: '状态',
        dataIndex: 'statusDisplay',
        width: 120,
        searchRender: {
          name: '当前状态',
          value: 'statuses',
          showCondition: false,
          type: FilterType.SELECT_MULTIPLE,
          filterComponentProps: {
            options: STATUS_OPTIONS.filter(s => s.value !== 'RETURNED').map((s) => ({ name: s.name, value: s.value })),
          },
        },
        render: (v: string, record: AccidentListItem) => {
          const status = record.status || '';
          const isReturned = record.flowState === 'RETURNED';
          const displayKey = isReturned ? 'RETURNED' : status;

          const name = STATUS_NAME_MAP[displayKey] || v || '--';
          const color = STATUS_COLOR_MAP[displayKey] || 'light-grey';

          return status ? <Tag color={color} text={name} /> : '--';
        },
      },
      {
        title: '登记时间',
        dataIndex: 'createTime',
        width: 200,
        searchRender: {
          name: '登记时间',
          value: 'createTimeRange',
          showCondition: false,
          type: FilterType.DATE_RANGE,
        },
        render: (v: string) => (v ? dayjs(v).format('YYYY-MM-DD') : '--'),
      },
      {
        title: '通知时间',
        dataIndex: 'notifyTime',
        width: 200,
        searchRender: {
          name: '通知时间',
          value: 'notifyTimeRange',
          showCondition: false,
          type: FilterType.DATE_RANGE,
        },
        render: (v: string) => (v ? dayjs(v).format('YYYY-MM-DD') : '--'),
      },
      {
        title: '整改截至时间',
        dataIndex: 'rectifyDeadline',
        width: 200,
        searchRender: {
          name: '整改截至时间',
          value: 'rectifyDeadlineRange',
          showCondition: false,
          type: FilterType.DATE_RANGE,
        },
        render: (v: string, record: any) => {
          if (!v) return '--';
          const deadline = dayjs(v);
          const diffDays = deadline.diff(dayjs(), 'day');
          const isSoon = deadline.isValid() && diffDays >= 0 && diffDays <= DEADLINE_SOON_DAYS && record?.status === '4';
          return (
            <Space size={8}>
              <span>{deadline.isValid() ? deadline.format('YYYY-MM-DD') : v}</span>
              {isSoon && <Tag color="light-red" size="small" text="即将截止" />}
            </Space>
          );
        },
      },
      {
        title: '操作',
        dataIndex: 'operation',
        width: 120,
        fixed: 'right' as const,
        render: (_: any, record: AccidentListItem) => {
          const ops = record.operationTypes || [];
          return (
            <Space size={12}>
              {/* 协同端操作(§4.3.1)：待处理/已驳回→处理(handle)；审批中/归档→查看详情(detail) */}
              {ops.includes('handle') && (
                <Button type="link" onClick={() => onProcess(record.oaNo)}>
                  处理
                </Button>
              )}
              {ops.includes('detail') && (
                <Button type="link" onClick={() => onViewDetail(record.accidentId)}>
                  查看详情
                </Button>
              )}
            </Space>
          );
        },
      },
    ],
    [onViewDetail, onProcess],
  );

  const tabItems = useMemo(
    () => [
      {
        key: TAB_KEYS.PENDING,
        tab: (
          <span>
            待处理
            <Tag text={counts?.PENDING ?? 0} size="xSmall" color="light-red" style={{ marginLeft: 6 }} />
          </span>
        ),
      },
      {
        key: TAB_KEYS.ALL,
        tab: (
          <span>
            全部
            <Tag text={counts?.ALL ?? 0} size="xSmall" style={{ marginLeft: 6 }} />
          </span>
        ),
      },
    ],
    [counts],
  );

  return (
    <ProCard title="事故管理" level={1} expandable={false}>
      <ProTablePlus
        ref={tableRef}
        rowKey="accidentId"
        columns={columns}
        request={{ queryService }}
        tableRefresh
        pagination={{ pageSize: 20, pageSizeOptions: [10, 20, 50, 100] }}
        scroll={{ x: 'max-content' }}
        toolbar={{
          tabSize: 'medium',
          tabs: {
            activeKey: activeTab,
            onChange: (key: string) => {
              setActiveTab(key as TabKey);
              tableRef.current?.query?.();
            },
            items: tabItems,
          },
          search: {
            placeholder: '事故编号',
            id: 'keyword',
          },
          filterSetting: {
            disableAddButton: true,
            showCondition: false,
          },
        }}
      />
    </ProCard>
  );
};

export default AccidentList;
